import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  SiteShell, PageHead, G, H1, P, PK, INK, SUB, LINE, CARD, BG2,
} from '../site/shell';

const wrap = { maxWidth:1000, margin:'0 auto' };
const sec  = { padding:'clamp(48px,7vw,80px) clamp(20px,5vw,48px)' };

/* ══════════════════════════════════════════════════════════════════
   PRODUCTS — single source of truth for both the list and each page.
   TODO(pmf): when a product goes live, set `status:'live'` and a real
   `storeHref`. `status:'soon'` shows it as "Coming soon" and disables
   the store button (the docs button still works).
   ══════════════════════════════════════════════════════════════════ */
export const PRODUCTS = [
  {
    slug:'unity-terraflux2d', name:'TerraFlux2D', platform:'Unity', accent:P,
    status:'live', store:'Unity Asset Store', storeHref:'https://assetstore.unity.com/packages/templates/systems/terraflux2d-357216',
    price:'$9.99', docHref:'/terraflux', img:'/coverterraflux.webp',
    tagline:'A procedural 2D world framework for Unity — deterministic, chunk-streamed and Mirror-ready.',
    problem:'Building an infinite 2D world with multiplayer means solving deterministic generation, chunk streaming and server sync from scratch. TerraFlux 2D is a server-authoritative framework that does exactly that over Mirror — infinite worlds with deterministic seeds, chunk streaming, seasons and persistent decorations, ready to extend with your own gameplay.',
    included:[
      'Deterministic procedural generation (same seed = same world)',
      'Chunk-based streaming — server-side generation, client-side rendering',
      'Biome system by height, temperature and moisture',
      'Persistent decorations + regenerating ground details',
      'Fully synchronized season system with smooth transitions',
      'Automatic world saving to JSON',
      'Demo scene showcasing all core features; clean documented code',
    ],
    requirements:[
      ['Unity','6.2 (6000.2.10) or newer'],
      ['Render pipeline','URP'],
      ['Dependency','Mirror Networking (free, not included)'],
      ['Version','1.1 · 14 MB'],
    ],
    faq:[
      ['Is it a complete game?','No — it’s a framework focused on logic and systems, meant to be extended by your own gameplay.'],
      ['Do I need Mirror?','Yes. Mirror Networking is required and free, but not included.'],
      ['Is it multiplayer-safe?','Yes — server-authoritative: generation, streaming and decorations run on the server; clients render received data. Safe for host or dedicated setups.'],
    ],
  },
  {
    slug:'unity-netinventory-pro', name:'NetInventory Pro', platform:'Unity', accent:P,
    status:'live', store:'Unity Asset Store', storeHref:'https://assetstore.unity.com/packages/tools/game-toolkits/netinventory-pro-375226',
    price:'$4.99', docHref:'/netinventory', img:'/coverinvenotry.webp',
    tagline:'Server-authoritative multiplayer inventory for Unity + Mirror — cheat-safe, drag & drop, save/load.',
    problem:'A multiplayer inventory that trusts the client is a cheat waiting to happen. NetInventory Pro runs every transaction — add, remove, swap, split, merge, drop, pick up — through the server, so it’s cheat-safe for competitive and co-op games. 40 slots, a polished drag-and-drop UI, world drops and JSON save/load, with items as ScriptableObjects (no code to add content).',
    included:[
      '40-slot inventory (8 hotbar + 32 grid) with full SyncList sync',
      'Server-authoritative transactions (add/remove/swap/split/merge/drop/pickup)',
      'Polished drag-and-drop UI with stack splitting and merging',
      'World item drops with automatic proximity pickup',
      'JSON save/load with per-player files',
      'Items as ScriptableObjects — no code to add content',
      'Pre-configured Player prefab + demo scene; clean documented API',
    ],
    requirements:[
      ['Unity','6.4 (6000.4.7) or newer'],
      ['Render pipeline','URP'],
      ['Dependency','Mirror Networking (free, not included)'],
      ['Version','1.0 · 574 KB'],
    ],
    faq:[
      ['Is it cheat-safe?','Yes — every item transaction runs through the server; the client can’t fake inventory state.'],
      ['Do I need to code to add items?','No. Items are ScriptableObjects — define new content without code.'],
      ['Do I need Mirror?','Yes. Mirror Networking is required and free, but not included.'],
    ],
  },
  {
    slug:'minecraft-cytags', name:'CyTags', platform:'Minecraft', accent:PK,
    status:'soon', store:'BuiltByBit', storeHref:'https://builtbybit.com/resources/cytag-custom-name-tags.125156/',
    price:'$4.99', docHref:'/cytags', img:'/covercytag.png',
    tagline:'Custom name tags — prefix & suffix — managed entirely from an in-game menu.',
    problem:'Giving players colored name tags usually means wrestling with a permissions plugin and YAML. CyTags shows a tag above the head, in the tab list and in chat, and lets staff manage everything by clicking in a GUI — no commands to memorize.',
    included:[
      'Tags above the head, in the tab list and in chat',
      'Full GUI editor — color, bold/italic, or MiniMessage gradients',
      'Reusable tag library: save, edit, rename, delete',
      'Auto-tags by rank via permission nodes',
      'PlaceholderAPI expansion (%cytags_prefix% / _suffix% / _tag%)',
      'Every player-facing message configurable in messages.yml',
    ],
    requirements:[
      ['Server','Paper / Spigot'],
      ['MC version','1.21 – 26.2'],
      ['Java','21+'],
      ['Optional','PlaceholderAPI'],
    ],
    faq:[
      ['Do I need PlaceholderAPI?','No — it’s optional, only needed to expose the tag to other chat/tab/scoreboard plugins.'],
      ['Do players have to type commands?','No. Everything is managed from the /tag menu by clicking.'],
      ['Does it work with LuckPerms?','Yes — permissions are ordinary Bukkit nodes, so any permissions plugin works.'],
    ],
  },
  {
    slug:'minecraft-cyadmin', name:'CyAdmin', platform:'Minecraft', accent:PK,
    status:'soon', store:'BuiltByBit', storeHref:'#',
    price:null, docHref:'/cyadmin', img:'/cyadmincover.png',
    tagline:'A full moderation & administration suite — punishments, player control, staff tools, reports & tickets — driven from a single /admin GUI.',
    problem:'A staff team needs a dozen plugins to ban, mute, freeze, vanish, view inventories, take reports and open tickets — each with its own config. CyAdmin is one plugin that does all of it, click-first: every action lives behind a single /admin panel, is stored, enforced and logged, and works across a whole network with MySQL, Velocity and Redis.',
    included:[
      'Full punishment system — ban, tempban, mute, tempmute, kick, warn — with reasons, durations and history',
      'Player control — teleport, gamemode, heal, feed, fly, god, speed, inventory view, clear, kill, repair',
      'Staff mode — vanish, freeze, staff chat, social & command spy, silent chest',
      'The /admin GUI — pick a player’s head and act, all by clicking',
      'Reports & tickets — players flag problems, staff resolve them from a queue',
      'Private notes on players + automatic suspicious-chat alerts',
      'Every action logged to file, with an optional Discord webhook',
      'Cross-server ready — shared MySQL, Velocity network bans and Redis live sync',
    ],
    requirements:[
      ['Server','Paper / Spigot'],
      ['MC version','1.21 – 26'],
      ['Java','21+'],
      ['Storage','SQLite / MySQL'],
      ['Optional','PlaceholderAPI · LuckPerms'],
    ],
    faq:[
      ['Do I need any other plugin?','No — CyAdmin runs on its own. PlaceholderAPI and LuckPerms are optional soft-depends.'],
      ['Does it work across a network?','Yes — point every server at the same MySQL, add the Velocity add-on for proxy-level bans, and enable Redis for instant live sync.'],
      ['Can staff use it without learning commands?','Yes. The whole toolkit is behind the /admin GUI; every button reuses the commands, so the same permissions and logging apply.'],
    ],
  },
];

const byPlatform = (p) => PRODUCTS.filter(x => x.platform === p);

/* ══════════════════════ PRODUCTS LIST ══════════════════════ */
function ProductCard({ p, i }) {
  const live = p.status === 'live';
  return (
    <Link to={`/produtos/${p.slug}`} className="work reveal" style={{ textDecoration:'none',background:CARD,border:`1px solid ${LINE}`,borderRadius:20,overflow:'hidden',display:'flex',flexDirection:'column',transitionDelay:`${i*70}ms` }}>
      <div style={{ height:170,position:'relative',overflow:'hidden',background:`linear-gradient(135deg,${p.accent}2e,${BG2})`,display:'flex',alignItems:'center',justifyContent:'center' }}>
        {p.img
          ? <img src={p.img} alt={p.name} loading="lazy" style={{ position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover' }} />
          : <span style={{ fontSize:12,letterSpacing:'.14em',textTransform:'uppercase',color:'rgba(255,255,255,.5)',fontWeight:600 }}>Demo coming soon</span>}
        <span style={{ position:'absolute',top:14,left:14,fontSize:11,fontWeight:700,color:'#fff',background:`${p.accent}`,padding:'4px 10px',borderRadius:999,letterSpacing:'.04em' }}>{p.platform}</span>
      </div>
      <div style={{ padding:'22px 24px 24px',display:'flex',flexDirection:'column',flex:1 }}>
        <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:10 }}>
          <span style={{ ...H1,fontSize:19,color:INK }}>{p.name}</span>
          <span style={{ marginLeft:'auto',display:'inline-flex',alignItems:'center',gap:6,fontSize:11,fontWeight:600,color:live?'#22C55E':'#E0A82E' }}>
            <span style={{ width:7,height:7,borderRadius:'50%',background:live?'#22C55E':'#E0A82E' }} />{live?'Available':'Coming soon'}
          </span>
        </div>
        <p style={{ fontSize:14,color:SUB,lineHeight:1.65,fontWeight:500,marginBottom:18,flex:1 }}>{p.tagline}</p>
        <div style={{ display:'flex',alignItems:'center',gap:10 }}>
          <span style={{ fontSize:14,fontWeight:600,color:P }}>See details →</span>
          {p.price && <span style={{ marginLeft:'auto',fontSize:13,fontWeight:700,color:INK,background:'rgba(163,102,255,.12)',border:`1px solid ${LINE}`,padding:'3px 10px',borderRadius:999 }}>{p.price}</span>}
        </div>
      </div>
    </Link>
  );
}

export function ProductsPage() {
  const groups = [
    ['Unity','Unity tools','For developers — server-authoritative multiplayer and systems.'],
    ['Minecraft','Minecraft plugins','For server owners — GUI configuration, no YAML.'],
  ].filter(([plat]) => byPlatform(plat).length > 0);
  return (
    <SiteShell active="produtos" title="Products" desc="Configurable Minecraft plugins from CywerStudio, each with a public changelog and full documentation.">
      <PageHead tag="Products" title="Tools that just" accent="plug in."
        lead="Configurable without code, with full documentation and a public changelog — you know what changes before you buy." />
      {groups.map(([plat,title,desc],gi) => (
        <section key={plat} style={{ ...sec, paddingTop:gi===0?0:undefined, background:gi%2?BG2:undefined, borderTop:gi%2?`1px solid ${LINE}`:undefined, borderBottom:gi%2?`1px solid ${LINE}`:undefined }}>
          <div style={{ maxWidth:1120,margin:'0 auto' }}>
            <div className="reveal" style={{ marginBottom:30 }}>
              <h2 style={{ ...H1,fontSize:'clamp(22px,3vw,30px)',color:INK,marginBottom:8 }}>{title}</h2>
              <p style={{ fontSize:15,color:SUB,fontWeight:500,maxWidth:560 }}>{desc}</p>
            </div>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,380px))',gap:18 }}>
              {byPlatform(plat).map((p,i) => <ProductCard key={p.slug} p={p} i={i} />)}
            </div>
          </div>
        </section>
      ))}
    </SiteShell>
  );
}

/* ══════════════════════ PRODUCT PAGE ══════════════════════ */
export function ProductPage() {
  const { slug } = useParams();
  const [open, setOpen] = useState(0);
  const p = PRODUCTS.find(x => x.slug === slug);
  if (!p) return <Navigate to="/produtos" replace />;
  const live = p.status === 'live';

  return (
    <SiteShell active="produtos" title={p.name} desc={p.tagline}>
      <header style={{ position:'relative',overflow:'hidden',padding:'clamp(112px,15vw,164px) clamp(20px,5vw,48px) clamp(32px,5vw,48px)' }}>
        <div className="glowA" style={{ position:'absolute',top:'-30%',left:'50%',transform:'translateX(-50%)',width:760,height:760,background:`radial-gradient(circle,${p.accent}22 0%,transparent 62%)`,pointerEvents:'none' }} />
        <div style={{ position:'relative',zIndex:1,...wrap }}>
          <Link to="/produtos" className="fl" style={{ marginBottom:18,display:'inline-block' }}>← Products</Link>
          <div className="hero-r" style={{ display:'flex',alignItems:'center',gap:10,marginBottom:16 }}>
            <span style={{ fontSize:11,fontWeight:700,color:'#fff',background:p.accent,padding:'5px 12px',borderRadius:999,letterSpacing:'.05em' }}>{p.platform}</span>
            <span style={{ display:'inline-flex',alignItems:'center',gap:6,fontSize:12,fontWeight:600,color:live?'#22C55E':'#E0A82E' }}>
              <span style={{ width:7,height:7,borderRadius:'50%',background:live?'#22C55E':'#E0A82E' }} />{live?'Available':'Coming soon'}
            </span>
          </div>
          {/* Problem in the first two lines */}
          <h1 className="hero-r" style={{ ...H1,fontSize:'clamp(32px,5vw,54px)',lineHeight:1.05,color:INK,animationDelay:'.1s',maxWidth:760 }}>{p.name}</h1>
          <p className="hero-r" style={{ fontSize:'clamp(16px,1.9vw,20px)',color:INK,lineHeight:1.55,fontWeight:600,maxWidth:640,marginTop:16,animationDelay:'.18s' }}>{p.tagline}</p>
          <p className="hero-r" style={{ fontSize:16,color:SUB,lineHeight:1.7,fontWeight:500,maxWidth:660,marginTop:14,animationDelay:'.26s' }}>{p.problem}</p>
        </div>
      </header>

      {/* Media above the fold (cover art / GIF / video) */}
      <section style={{ padding:'0 clamp(20px,5vw,48px) clamp(40px,6vw,64px)' }}>
        {p.img
          ? <div className="reveal" style={{ ...wrap,aspectRatio:'16/9',borderRadius:20,border:`1px solid ${LINE}`,overflow:'hidden' }}>
              <img src={p.img} alt={p.name} style={{ width:'100%',height:'100%',objectFit:'cover',display:'block' }} />
            </div>
          : <div className="reveal" style={{ ...wrap,aspectRatio:'16/9',borderRadius:20,border:`1px solid ${LINE}`,background:`linear-gradient(135deg,${p.accent}1f,${BG2})`,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:10 }}>
              <div style={{ fontSize:34 }}>▶</div>
              <span style={{ fontSize:13,letterSpacing:'.14em',textTransform:'uppercase',color:SUB,fontWeight:600 }}>Working demo — coming soon</span>
            </div>}
      </section>

      {/* Included + Requirements */}
      <section style={{ ...sec, paddingTop:0 }}>
        <div className="two-col" style={{ ...wrap,display:'grid',gridTemplateColumns:'1.3fr 1fr',gap:'clamp(24px,4vw,44px)',alignItems:'start' }}>
          <div className="reveal">
            <h2 style={{ ...H1,fontSize:'clamp(22px,3vw,28px)',color:INK,marginBottom:20 }}>What's <G>included</G></h2>
            <div style={{ display:'flex',flexDirection:'column',gap:12 }}>
              {p.included.map((it,idx) => (
                <div key={idx} style={{ display:'flex',gap:12,alignItems:'flex-start',background:CARD,border:`1px solid ${LINE}`,borderRadius:14,padding:'16px 18px' }}>
                  <span style={{ color:p.accent,fontWeight:800,flexShrink:0,marginTop:1 }}>✓</span>
                  <span style={{ fontSize:15,color:INK,lineHeight:1.6,fontWeight:500 }}>{it}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="reveal">
            <h2 style={{ ...H1,fontSize:'clamp(22px,3vw,28px)',color:INK,marginBottom:20 }}>Requirements</h2>
            <div style={{ background:CARD,border:`1px solid ${LINE}`,borderRadius:16,overflow:'hidden' }}>
              {p.requirements.map(([k,v],idx) => (
                <div key={k} style={{ display:'flex',justifyContent:'space-between',gap:16,padding:'15px 18px',borderBottom:idx<p.requirements.length-1?`1px solid ${LINE}`:'none' }}>
                  <span style={{ fontSize:13,color:SUB,fontWeight:600 }}>{k}</span>
                  <span style={{ fontSize:14,color:INK,fontWeight:600,textAlign:'right' }}>{v}</span>
                </div>
              ))}
            </div>
            {p.docHref && <a href={p.docHref} target="_blank" rel="noreferrer" className="fl" style={{ display:'inline-block',marginTop:16 }}>Read the full documentation →</a>}
            <Link to="/changelog" className="fl" style={{ display:'block',marginTop:10 }}>See this product's changelog →</Link>
          </div>
        </div>
      </section>

      {/* Product-specific FAQ */}
      <section style={{ ...sec, background:BG2, borderTop:`1px solid ${LINE}` }}>
        <div style={{ maxWidth:820,margin:'0 auto' }}>
          <h2 className="reveal" style={{ ...H1,fontSize:'clamp(22px,3vw,30px)',color:INK,marginBottom:24 }}>Frequently <G>asked</G></h2>
          <div className="reveal" style={{ background:CARD,border:`1px solid ${LINE}`,borderRadius:18,padding:'6px 26px' }}>
            {p.faq.map(([q,a],idx) => (
              <div key={idx} onClick={() => setOpen(open===idx?null:idx)} style={{ borderBottom:idx<p.faq.length-1?`1px solid ${LINE}`:'none',padding:'20px 0',cursor:'pointer' }}>
                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',gap:12 }}>
                  <p style={{ fontWeight:700,fontSize:16,color:INK }}>{q}</p>
                  <span style={{ fontSize:22,color:open===idx?P:'#55555f',transition:'transform .2s,color .2s',transform:open===idx?'rotate(45deg)':'none',flexShrink:0,lineHeight:1 }}>+</span>
                </div>
                {open===idx && <p style={{ fontSize:15,color:SUB,lineHeight:1.7,fontWeight:500,marginTop:11 }}>{a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Store CTA */}
      <section style={{ padding:'clamp(48px,7vw,86px) clamp(20px,5vw,48px)' }}>
        <div className="reveal" style={{ ...wrap,textAlign:'center',background:CARD,border:`1px solid ${LINE}`,borderRadius:22,padding:'clamp(40px,6vw,64px) clamp(24px,5vw,48px)' }}>
          <h2 style={{ ...H1,fontSize:'clamp(24px,3.4vw,34px)',color:INK,marginBottom:10 }}>{live?`Available on the ${p.store}`:`Coming soon to the ${p.store}`}</h2>
          <p style={{ fontSize:15,color:SUB,fontWeight:500,maxWidth:440,margin:'0 auto 26px',lineHeight:1.65 }}>
            {live
              ? `${p.price ? `${p.price}. ` : ''}Purchase and updates through the ${p.store}.`
              : `We're preparing the ${p.store} page${p.price ? ` — ${p.price}` : ''}.${p.docHref ? ' The documentation is already available.' : " Want to know when it's out?"}`}
          </p>
          {(() => {
            const storeReal = p.storeHref && p.storeHref !== '#';
            const storeLive = live && storeReal;
            return (
              <div style={{ display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap' }}>
                {storeLive && <a href={p.storeHref} target="_blank" rel="noreferrer" className="btn-p">Open on the {p.store} →</a>}
                {p.docHref && <a href={p.docHref} target="_blank" rel="noreferrer" className="btn-p">Read the documentation →</a>}
                {!storeLive && <span className="btn-d">{live ? `On ${p.store}` : `Coming soon to ${p.store}`}</span>}
                {!p.docHref && !live && <Link to="/contacto" className="btn-g">Notify me when it's out</Link>}
              </div>
            );
          })()}
        </div>
      </section>
    </SiteShell>
  );
}
